import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { RIVIERA_CITIES, NL_AREAS } from "@/lib/riviera";
import PartialTasksPicker, { hasPartialSelection, partialTasksPayload } from "@/components/PartialTasksPicker";
import { useTranslation } from "react-i18next";
import { api, SERVICES, CUSTOM_QUOTE_SERVICES, ADDONS_SERVICES, addonsFor, RECURRING_SERVICES, PARTIAL_SERVICE, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles, Home, Calendar, ClipboardList, CheckCircle2, Info, ArrowRight, ArrowLeft } from "lucide-react";

// Frequency chip config — labels resolved via i18n at render time.
const FREQ_OPTIONS_DEFAULT = [
  { value: "one-time", labelKey: "book_v2.frequency.one_time", pct: 0 },
  { value: "weekly", labelKey: "book_v2.frequency.weekly", pct: 15 },
  { value: "bi-weekly", labelKey: "book_v2.frequency.bi_weekly", pct: 10 },
  { value: "monthly", labelKey: "book_v2.frequency.monthly", pct: 5 },
  { value: "custom", labelKey: "book_v2.frequency.custom", pct: 0 },
];
const FREQ_OPTIONS_DEEP = [
  { value: "one-time", labelKey: "book_v2.frequency.one_off", pct: 0 },
  { value: "monthly", labelKey: "book_v2.frequency.monthly_deep", pct: 10 },
  { value: "quarterly", labelKey: "book_v2.frequency.quarterly", pct: 5 },
];

function getFrequencyOptions(serviceType) {
  return serviceType === "Deep Cleaning" ? FREQ_OPTIONS_DEEP : FREQ_OPTIONS_DEFAULT;
}

function fmtDuration(hours) {
  const h = Number(hours) || 0;
  const w = Math.floor(h);
  const m = Math.round((h - w) * 60);
  if (w > 0 && m > 0) return `${w}h ${m}min`;
  if (w > 0) return `${w}h`;
  if (m > 0) return `${m}min`;
  return "—";
}

const FR_BOOKABLE = RIVIERA_CITIES.filter((c) => !c.inquiryOnly);

export default function Book({ country = "NL" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isFR = country === "FR";
  const { t } = useTranslation();
  const STEPS = [
    { key: "service", labelKey: "book_v2.steps.service", icon: Sparkles },
    { key: "property", labelKey: "book_v2.steps.property", icon: Home },
    { key: "schedule", labelKey: "book_v2.steps.schedule", icon: Calendar },
    { key: "review", labelKey: "book_v2.steps.review", icon: CheckCircle2 },
  ];
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    service_type: "Deep Cleaning",
    frequency: "one-time",
    addons: [],
    partial_tasks: {},
    sqm: "",
    floors: 1,
    bedrooms: 1,
    bathrooms: 1,
    client_type: "Residential / Private",
    date: "",
    start_time: "10:00",
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    address: "",
    city: isFR ? ((FR_BOOKABLE.find((c) => c.slug === params.get("city")) || FR_BOOKABLE[0]).name) : (NL_AREAS.find((c) => c.slug === params.get("city"))?.city || ""),
    postal_code: "",
    access_instructions: "",
    special_requests: "",
    agree_cancellation: false,
    agree_disclaimer: false,
  });
  const [pricing, setPricing] = useState(null);
  const [calc, setCalc] = useState(false);
  const [discountInput, setDiscountInput] = useState(() => {
    try { return localStorage.getItem("trustline_discount_code") || ""; } catch { return ""; }
  });
  const [discountApplied, setDiscountApplied] = useState(null);
  const [discountMsg, setDiscountMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [frVat, setFrVat] = useState(null);
  useEffect(() => {
    if (isFR) api.get("/public/settings?country=FR").then((r) => setFrVat(r.data.vat_category
      ? { exempt: r.data.vat_exempt, res: r.data.vat_category_rate, b2b: r.data.vat_category_rate }
      : { exempt: false, res: r.data.vat_residential_pct, b2b: r.data.vat_commercial_pct })).catch(() => {});
  }, [isFR]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCustomQuote = CUSTOM_QUOTE_SERVICES.includes(form.service_type);
  const supportsFrequency = RECURRING_SERVICES.includes(form.service_type);
  const supportsExtras = ADDONS_SERVICES.includes(form.service_type);
  const isPartial = form.service_type === PARTIAL_SERVICE;
  const postalOk = (isFR ? /^\d{5}$/ : /^\d{4}\s?[A-Za-z]{2}$/).test(form.postal_code.trim());
  const requiresRooms = !isCustomQuote && !isPartial && !["Pressure Washing", "Garden / Terrace Cleaning", "Window Cleaning"].includes(form.service_type);

  // If frequency isn't supported (or invalid for new service), fall back to one-time.
  useEffect(() => {
    const opts = getFrequencyOptions(form.service_type).map((o) => o.value);
    if (!supportsFrequency && form.frequency !== "one-time") upd("frequency", "one-time");
    else if (supportsFrequency && !opts.includes(form.frequency)) upd("frequency", "one-time");
    if (!supportsExtras && form.addons.length) upd("addons", []);
    else {
      const allowed = addonsFor(form.service_type).map((x) => x.key);
      if (form.addons.some((k) => !allowed.includes(k))) upd("addons", form.addons.filter((k) => allowed.includes(k)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_type]);

  // Recalculate estimate
  useEffect(() => {
    if (isCustomQuote) { setPricing(null); return; }
    const payload = {
      service_type: form.service_type,
      sqm: parseFloat(form.sqm) || 0,
      floors: parseInt(form.floors) || 1,
      frequency: form.frequency,
      client_type: form.client_type,
      country,
    };
    if (supportsExtras && form.addons.length) payload.addons = form.addons;
    if (isPartial) payload.partial_tasks = partialTasksPayload(form.partial_tasks);
    if (discountApplied?.code) payload.discount_code = discountApplied.code;
    setCalc(true);
    api.post("/pricing/calculate", payload)
      .then((r) => setPricing(r.data))
      .catch((e) => {
        setPricing(null);
        const msg = e?.response?.data?.detail;
        if (msg === "Only one discount can be applied to this booking.") {
          setDiscountApplied(null);
          setDiscountMsg({ type: "error", text: t("book_v2.toast.discount_conflict") });
        }
      })
      .finally(() => setCalc(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_type, form.sqm, form.floors, form.frequency, form.client_type, form.addons, form.partial_tasks, discountApplied, country]);

  const applyDiscount = async () => {
    const code = (discountInput || "").trim();
    if (!code) return;
    if (supportsFrequency && form.frequency !== "one-time") {
      setDiscountMsg({ type: "error", text: t("book_v2.toast.discount_conflict") });
      return;
    }
    try {
      const r = await api.post("/discounts/validate", { code, customer_email: form.customer_email || undefined, country });
      if (r.data?.valid) {
        setDiscountApplied({ code: r.data.code, value_pct: r.data.value_pct });
        setDiscountMsg({ type: "success", text: r.data.message });
      } else {
        setDiscountApplied(null);
        setDiscountMsg({ type: "error", text: r.data?.message || "Invalid or expired discount code" });
      }
    } catch { setDiscountMsg({ type: "error", text: "Invalid or expired discount code" }); }
  };

  // Determine "Confirm Booking" vs "Request Booking" based on 7-day rule
  const daysAhead = useMemo(() => {
    if (!form.date) return 0;
    const chosen = new Date(form.date + "T00:00");
    return (chosen - new Date()) / (1000 * 60 * 60 * 24);
  }, [form.date]);
  const isConfirmBooking = daysAhead >= 7;

  const submit = async () => {
    if (!form.agree_cancellation || !form.agree_disclaimer) {
      toast.error("Please acknowledge the policy and disclaimer.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        sqm: parseFloat(form.sqm) || 0,
        floors: parseInt(form.floors) || 1,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        is_commercial: form.client_type === "Commercial / B2B",
        customer_email: (form.customer_email || "").trim().toLowerCase(),
        discount_code: discountApplied?.code || undefined,
        addons: supportsExtras && form.addons.length ? form.addons : undefined,
        partial_tasks: isPartial ? partialTasksPayload(form.partial_tasks) : undefined,
        country,
      };
      const r = await api.post("/bookings/checkout", payload);
      setConfirmation({ ...r.data, ...pricing, ...form });
      toast.success(isConfirmBooking ? t("book_v2.toast.confirmed") : t("book_v2.toast.request_ok"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  const canNext = () => {
    if (step === 0) return !!form.service_type && (!isPartial || hasPartialSelection(form.partial_tasks, form.addons));
    if (step === 1) return isCustomQuote || isPartial || (!!form.sqm && Number(form.sqm) > 0);
    if (step === 2) return form.date && form.start_time && form.customer_name && form.customer_email && form.customer_phone && form.address && form.city && postalOk;
    return true;
  };

  if (confirmation) return <ConfirmationScreen t={t} c={confirmation} isConfirm={isConfirmBooking} nav={nav} />;

  return (
    <div className="min-h-screen bg-slate-50 py-8 md:py-14">
      {isFR && (
        <div className="max-w-6xl mx-auto px-4 mb-6" data-testid="book-fr-banner">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#0EA5E9]">{t("country.fr")}</p>
          <h1 className="text-3xl sm:text-4xl text-[#0F172A] mt-1">{t("riviera.book.title")}</h1>
          <p className="text-sm text-slate-600 mt-2">{t("riviera.book.subtitle")} <Link to="/fr/inquiry" className="text-[#0EA5E9] font-semibold underline" data-testid="book-fr-quote-link">{t("riviera.cta_inquiry")}</Link></p>
          <p className="text-xs text-sky-800 bg-sky-50 ring-1 ring-sky-200 rounded-lg px-3 py-2 mt-3 inline-block" data-testid="book-fr-confirm-note">{t("riviera.book.confirm_note")}</p>
          <p className="text-[11px] text-slate-500 mt-2" lang="fr" data-testid="book-fr-legal-links">
            <Link to="/fr/confidentialite" className="underline">Politique de confidentialité</Link> · <Link to="/fr/retractation" className="underline">Droit de rétractation</Link>
          </p>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="min-w-0 bg-white rounded-2xl ring-1 ring-slate-200 p-6 md:p-8">
          {/* Progress */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1" data-testid="book-steps">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i < step;
              const active = i === step;
              return (
                <div key={s.key} className="flex items-center gap-1 shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    active ? "bg-[#1B3A6B] text-white" : done ? "bg-[#5BA83F] text-white" : "bg-slate-100 text-slate-400"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`text-xs font-semibold ${active ? "text-[#1B3A6B]" : "text-slate-500"}`}>{t(s.labelKey)}</span>
                  {i < STEPS.length - 1 && <div className="w-4 h-px bg-slate-200" />}
                </div>
              );
            })}
          </div>

          {step === 0 && <StepService t={t} form={form} upd={upd} supportsFrequency={supportsFrequency} supportsExtras={supportsExtras} isCustomQuote={isCustomQuote} isPartial={isPartial} />}
          {step === 1 && <StepProperty t={t} form={form} upd={upd} isCustomQuote={isCustomQuote} requiresRooms={requiresRooms} frVat={frVat} />}
          {step === 2 && <StepSchedule t={t} form={form} upd={upd} isConfirmBooking={isConfirmBooking} isFR={isFR} postalOk={postalOk} />}
          {step === 3 && (
            <StepReview t={t} form={form} upd={upd} pricing={pricing} isCustomQuote={isCustomQuote}
              discountInput={discountInput} setDiscountInput={setDiscountInput}
              discountApplied={discountApplied} discountMsg={discountMsg}
              applyDiscount={applyDiscount} removeDiscount={() => { setDiscountApplied(null); setDiscountMsg(null); setDiscountInput(""); }}
            />
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
            <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="book-prev">
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("book_v2.buttons.back")}
            </Button>
            {step < 3 ? (
              <Button onClick={() => setStep((s) => Math.min(3, s + 1))} disabled={!canNext()}
                className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="book-next">
                {t("book_v2.buttons.next")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting || !form.agree_cancellation || !form.agree_disclaimer}
                className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full px-8"
                data-testid="book-submit"
              >
                {submitting ? t("book_v2.buttons.submitting") : (isConfirmBooking ? t("book_v2.buttons.confirm") : t("book_v2.buttons.request"))}
              </Button>
            )}
          </div>
        </div>

        <EstimateBox t={t} pricing={pricing} form={form} calc={calc} isCustomQuote={isCustomQuote}
          storeCredit={Number(user?.store_credit_eur || 0)}
          discountInput={discountInput} setDiscountInput={setDiscountInput}
          discountApplied={discountApplied} discountMsg={discountMsg}
          applyDiscount={applyDiscount} removeDiscount={() => { setDiscountApplied(null); setDiscountMsg(null); setDiscountInput(""); }}
        />
      </div>
    </div>
  );
}

/* ===== Step 0: Service + Frequency + Extras ===== */
function StepService({ t, form, upd, supportsFrequency, supportsExtras, isCustomQuote, isPartial }) {
  const freqOptions = getFrequencyOptions(form.service_type);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t("book_v2.service.title")}</h2>
        <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold mt-4 block">{t("book_v2.service.label")}</Label>
        <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
          <SelectTrigger className="mt-1.5" data-testid="book-service"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s}>{s}{CUSTOM_QUOTE_SERVICES.includes(s) ? ` ${t("book_v2.service.custom_quote_suffix")}` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isCustomQuote && (
          <div className="mt-3 rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
            {t("book_v2.service.custom_quote_banner")}
          </div>
        )}
      </div>

      {supportsFrequency && (
        <div>
          <h3 className="font-heading font-bold text-[#1B3A6B]">{t("book_v2.frequency.title")}</h3>
          <p className="text-xs text-slate-500 mb-3">{t("book_v2.frequency.help")}</p>
          <div className={`grid gap-2 ${freqOptions.length <= 3 ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-5"}`} data-testid="book-frequency">
            {freqOptions.map((f) => (
              <button
                type="button"
                key={f.value}
                onClick={() => upd("frequency", f.value)}
                className={`rounded-lg text-xs px-3 py-3 ring-1 transition ${
                  form.frequency === f.value
                    ? "bg-[#E8F5E0] ring-[#5BA83F] text-[#1F4D0F]"
                    : "bg-white ring-slate-200 text-slate-700 hover:ring-slate-300"
                }`}
                data-testid={`book-frequency-${f.value}`}
              >
                <div className="font-semibold">{t(f.labelKey)}</div>
                {f.pct > 0 && <div className="text-[10px] text-emerald-700 mt-0.5">−{f.pct}%</div>}
              </button>
            ))}
          </div>
        </div>
      )}

      {isPartial && (
        <div data-testid="book-partial-section">
          <h3 className="font-heading font-bold text-[#1B3A6B]">{t("partial.title")}</h3>
          <p className="text-xs text-slate-500 mb-3">{t("partial.help")}</p>
          <PartialTasksPicker value={form.partial_tasks} onChange={(v) => upd("partial_tasks", v)} testidPrefix="book-partial" />
          {!hasPartialSelection(form.partial_tasks, form.addons) && <p className="text-xs text-amber-700 mt-2" data-testid="book-partial-need-one">{t("partial.need_one")}</p>}
        </div>
      )}

      {supportsExtras && (
        <div>
          <h3 className="font-heading font-bold text-[#1B3A6B]">{t("book_v2.extras.title")}</h3>
          <p className="text-xs text-slate-500 mb-3">{t("book_v2.extras.help")}</p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="book-addons">
            {addonsFor(form.service_type).map((a) => {
              const checked = form.addons.includes(a.key);
              return (
                <label key={a.key}
                  className={`cursor-pointer rounded-lg px-4 py-3 ring-1 transition ${
                    checked ? "bg-[#E8F5E0] ring-[#5BA83F]" : "bg-white ring-slate-200 hover:ring-slate-300"
                  }`}
                  data-testid={`book-addon-${a.key}`}
                >
                  <input type="checkbox" className="sr-only" checked={checked}
                    onChange={(e) => upd("addons", e.target.checked ? [...form.addons, a.key] : form.addons.filter((k) => k !== a.key))}
                  />
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-[#0F172A]">{t(`book_v2.extra_labels.${a.key}`, a.label)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{t("book_v2.extras.unit_minutes", { n: a.minutes })}{a.note ? ` · ${a.note}` : ""}</div>
                    </div>
                    {checked && <CheckCircle2 className="w-4 h-4 text-[#5BA83F]" />}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Step 2: Property ===== */
function StepProperty({ t, form, upd, isCustomQuote, requiresRooms, frVat }) {
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t("book_v2.property.title")}</h2>
      <p className="text-xs text-slate-500">{t("book_v2.property.help")}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>{t("book_v2.property.sqm")}</Label>
          <Input type="number" min="0" placeholder={t("book_v2.property.sqm_ph")} value={form.sqm} onChange={(e) => upd("sqm", e.target.value)} className="mt-1.5" data-testid="book-sqm" />
        </div>
        <div>
          <Label>{t("book_v2.property.floors")}</Label>
          <Select value={String(form.floors)} onValueChange={(v) => upd("floors", parseInt(v))}>
            <SelectTrigger className="mt-1.5" data-testid="book-floors"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6].map((n) => (
                <SelectItem key={n} value={String(n)}>{n === 6 ? "6+" : n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {requiresRooms && (
          <>
            <div>
              <Label>{t("book_v2.property.bedrooms")}</Label>
              <Input type="number" min="0" value={form.bedrooms} onChange={(e) => upd("bedrooms", parseInt(e.target.value) || 0)} className="mt-1.5" data-testid="book-bedrooms" />
            </div>
            <div>
              <Label>{t("book_v2.property.bathrooms")}</Label>
              <Input type="number" min="0" value={form.bathrooms} onChange={(e) => upd("bathrooms", parseInt(e.target.value) || 0)} className="mt-1.5" data-testid="book-bathrooms" />
            </div>
          </>
        )}
        <div>
          <Label>{t("book_v2.property.client_type")}</Label>
          <Select value={form.client_type} onValueChange={(v) => upd("client_type", v)}>
            <SelectTrigger className="mt-1.5" data-testid="book-client-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Residential / Private">{frVat ? t(frVat.exempt ? "riviera.book.client_res_exempt" : "riviera.book.client_res", { pct: frVat.res }) : t("book_v2.property.client_type_residential")}</SelectItem>
              <SelectItem value="Commercial / B2B">{frVat ? t(frVat.exempt ? "riviera.book.client_b2b_exempt" : "riviera.book.client_b2b", { pct: frVat.b2b }) : t("book_v2.property.client_type_commercial")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isCustomQuote && (
        <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-600">
          {t("book_v2.property.custom_quote_note")}
        </div>
      )}
    </div>
  );
}

/* ===== Step 3: Schedule + Customer ===== */
function StepSchedule({ t, form, upd, isConfirmBooking, isFR, postalOk }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t("book_v2.schedule.title")}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>{t("book_v2.schedule.date")} <span className="text-rose-500">*</span></Label>
          <Input type="date" value={form.date} onChange={(e) => upd("date", e.target.value)} className="mt-1.5" data-testid="book-date" min={new Date().toISOString().slice(0,10)} />
        </div>
        <div>
          <Label>{t("book_v2.schedule.time")} <span className="text-rose-500">*</span></Label>
          <Input type="time" value={form.start_time} onChange={(e) => upd("start_time", e.target.value)} className="mt-1.5" data-testid="book-time" />
        </div>
      </div>
      {form.date && !isConfirmBooking && (
        <div className="rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900" data-testid="book-request-notice"
             dangerouslySetInnerHTML={{ __html: t("book_v2.schedule.request_notice") }} />
      )}

      <div className="pt-2 border-t border-slate-100">
        <h3 className="font-heading font-bold text-[#1B3A6B] mb-3">{t("book_v2.schedule.customer_title")}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("book_v2.schedule.name")} <span className="text-rose-500">*</span></Label>
            <Input value={form.customer_name} onChange={(e) => upd("customer_name", e.target.value)} className="mt-1.5" data-testid="book-name" />
          </div>
          <div>
            <Label>{t("book_v2.schedule.email")} <span className="text-rose-500">*</span></Label>
            <Input type="email" value={form.customer_email} onChange={(e) => upd("customer_email", e.target.value)} className="mt-1.5" data-testid="book-email" />
          </div>
          <div>
            <Label>{t("book_v2.schedule.phone")} <span className="text-rose-500">*</span></Label>
            <Input value={form.customer_phone} onChange={(e) => upd("customer_phone", e.target.value)} placeholder={isFR ? "+33 ..." : "+31 ..."} className="mt-1.5" data-testid="book-phone" />
          </div>
          <div>
            <Label>{t("book_v2.schedule.city")} <span className="text-rose-500">*</span></Label>
            {isFR ? (
              <Select value={form.city} onValueChange={(v) => upd("city", v)}>
                <SelectTrigger className="mt-1.5" data-testid="book-city"><SelectValue /></SelectTrigger>
                <SelectContent>{FR_BOOKABLE.map((c) => <SelectItem key={c.slug} value={c.name} data-testid={`book-city-option-${c.slug}`}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="Amsterdam" className="mt-1.5" data-testid="book-city" />
            )}
            {isFR && <p className="text-[11px] text-amber-700 mt-1" data-testid="book-monaco-note">{t("riviera.book.monaco_hint")}</p>}
          </div>
          <div className="sm:col-span-2">
            <Label>{t("book_v2.schedule.address")} <span className="text-rose-500">*</span></Label>
            <Input value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder={t("book_v2.schedule.address_ph")} className="mt-1.5" data-testid="book-address" />
          </div>
          <div>
            <Label>{t("book_v2.schedule.postal")}</Label>
            <Input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} placeholder={isFR ? "06000" : "1011 AB"} className="mt-1.5" data-testid="book-postal" />
            {form.postal_code.trim() && !postalOk && <p className="text-[11px] text-rose-600 mt-1" data-testid="book-postal-error">{t(isFR ? "nl_city_page.postal_error_fr" : "nl_city_page.postal_error_nl")}</p>}
          </div>
        </div>
        <div className="mt-4">
          <Label>{t("book_v2.schedule.access")}</Label>
          <Textarea value={form.access_instructions} onChange={(e) => upd("access_instructions", e.target.value)} className="mt-1.5" placeholder={t("book_v2.schedule.access_ph")} />
        </div>
      </div>
    </div>
  );
}

/* ===== Step 4: Review + Acknowledgements ===== */
function StepReview({ t, form, upd, pricing, isCustomQuote, discountInput, setDiscountInput, discountApplied, discountMsg, applyDiscount, removeDiscount }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t("book_v2.review.title")}</h2>

      {!discountApplied && !isCustomQuote && (
        <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-4">
          <Label className="text-xs uppercase tracking-wide font-semibold text-slate-500">{t("book_v2.review.discount_label")}</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={discountInput} onChange={(e) => setDiscountInput(e.target.value.toUpperCase())} placeholder={t("book_v2.review.discount_ph")} data-testid="book-discount-input" />
            <Button type="button" onClick={applyDiscount} className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white" data-testid="book-discount-apply">{t("book_v2.review.apply")}</Button>
          </div>
          {discountMsg && <div className={`mt-2 text-xs ${discountMsg.type === "success" ? "text-emerald-700" : "text-rose-600"}`} data-testid="book-discount-message">{discountMsg.text}</div>}
        </div>
      )}
      {discountApplied && (
        <div className="rounded-lg bg-[#E8F5E0] ring-1 ring-[#5BA83F]/40 px-4 py-2.5 flex items-center justify-between" data-testid="book-discount-applied">
          <span className="text-sm font-semibold text-[#1F4D0F]">{t("book_v2.review.applied_line", { code: discountApplied.code, pct: discountApplied.value_pct })}</span>
          <button type="button" onClick={removeDiscount} className="text-xs text-slate-600 hover:text-slate-900">{t("book_v2.review.remove")}</button>
        </div>
      )}

      {/* Cancellation policy */}
      <section className="bg-white ring-1 ring-slate-200 rounded-lg p-4" data-testid="book-cancellation-policy">
        <h3 className="font-heading font-bold text-[#1B3A6B]">{t("book_v2.review.policy_title")}</h3>
        <div className="mt-2 space-y-2 text-xs text-slate-600 leading-relaxed">
          <p dangerouslySetInnerHTML={{ __html: t("book_v2.review.policy_free") }} />
          <p dangerouslySetInnerHTML={{ __html: t("book_v2.review.policy_same_day") }} />
          <p dangerouslySetInnerHTML={{ __html: t("book_v2.review.policy_no_show") }} />
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
          <a href="/terms" target="_blank" rel="noreferrer" className="text-[#1B3A6B] underline">{t("book_v2.review.terms_link")}</a>
          <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#1B3A6B] underline">{t("book_v2.review.privacy_link")}</a>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-white ring-1 ring-slate-200 rounded-lg p-4" data-testid="book-disclaimer">
        <div className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
          <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
          <div>
            <p dangerouslySetInnerHTML={{ __html: isCustomQuote ? t("book_v2.review.disclaimer_custom") : t("book_v2.review.disclaimer_duration") }} />
          </div>
        </div>
      </section>

      {/* Acknowledgements */}
      <div className="space-y-3">
        <label className="flex gap-3 items-start cursor-pointer">
          <Checkbox checked={form.agree_cancellation} onCheckedChange={(v) => upd("agree_cancellation", !!v)} className="mt-0.5" data-testid="book-agree-cancel" />
          <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: t("book_v2.review.agree_cancel") }} />
        </label>
        <label className="flex gap-3 items-start cursor-pointer">
          <Checkbox checked={form.agree_disclaimer} onCheckedChange={(v) => upd("agree_disclaimer", !!v)} className="mt-0.5" data-testid="book-agree-disclaimer" />
          <span className="text-sm text-slate-700">{t("book_v2.review.agree_disclaimer")}</span>
        </label>
      </div>

      {/* Summary */}
      {pricing && (
        <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-4 text-sm space-y-1">
          <div className="flex justify-between"><span>{t("book_v2.review.summary_service")}</span><b>{form.service_type}</b></div>
          {pricing.partial_tasks?.length > 0 && (
            <div className="flex justify-between gap-4"><span>{t("partial.areas")}</span><b className="text-right" data-testid="book-review-areas">{pricing.partial_tasks.map((x) => `${t(`partial.tasks.${x.key}`)}${x.qty > 1 ? ` ×${x.qty}` : ""}`).join(", ")}</b></div>
          )}
          <div className="flex justify-between"><span>{t("book_v2.review.summary_duration")}</span><b data-testid="book-review-duration">{fmtDuration(pricing.billable_hours || pricing.hours)}</b></div>
          <div className="flex justify-between"><span>{t("book_v2.review.summary_total")}</span><b className="text-[#1B3A6B]">€{pricing.total?.toFixed(2)}</b></div>
        </div>
      )}
    </div>
  );
}

/* ===== Sticky Estimate Box ===== */
function EstimateBox({ t, pricing, form, calc, isCustomQuote, storeCredit = 0, discountInput, setDiscountInput, discountApplied, discountMsg, applyDiscount, removeDiscount }) {
  const p = pricing;
  const creditApplied = storeCredit > 0 && p ? Math.min(storeCredit, Number(p.total || 0)) : 0;
  return (
    <aside className="lg:sticky lg:top-24 h-fit bg-[#1B3A6B] text-white rounded-2xl p-6 shadow-sm" data-testid="book-estimate">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#9BD478]">
        <Sparkles className="w-3.5 h-3.5" /> {t("book_v2.estimate.eyebrow")}
      </div>
      <h3 className="font-heading text-xl font-bold mt-1">{isCustomQuote ? t("book_v2.estimate.title_custom") : t("book_v2.estimate.title_booking")}</h3>

      {isCustomQuote ? (
        <p className="text-sm text-slate-200 mt-3">{t("book_v2.estimate.custom_body")}</p>
      ) : (
        <>
          <div className="mt-4 text-sm space-y-1.5">
            <Line k={t("book_v2.estimate.service")} v={form.service_type} />
            {form.frequency !== "one-time" && <Line k={t("book_v2.estimate.frequency")} v={form.frequency.replace("-", " ")} />}
            {form.sqm && <Line k={t("book_v2.estimate.property")} v={`${form.sqm} m²`} />}
            <Line k={t("book_v2.estimate.floors")} v={String(form.floors) + (form.floors === 6 ? "+" : "")} />
            {p?.addons?.length ? (
              <div className="flex justify-between gap-4 text-sm" data-testid="book-estimate-extras">
                <span className="text-slate-300 shrink-0">{t("book_v2.estimate.extras_label")}</span>
                <span className="text-right">{p.addons.map((a) => t(`book_v2.extra_labels.${a.key}`, a.label || a.key)).join(", ")}</span>
              </div>
            ) : null}
          </div>

          <div className="mt-4 border-t border-white/10 pt-3 text-sm space-y-1.5">
            {calc ? (
              <div className="text-xs text-slate-300">{t("book_v2.estimate.calculating")}</div>
            ) : p ? (
              <>
                <Line k={t("book_v2.estimate.base")} v={`€${p.base_subtotal_excl_vat?.toFixed(2)}`} />
                {p.floor_surcharge_excl_vat > 0 && <Line k={t("book_v2.estimate.floor_surcharge", { pct: p.floor_surcharge_pct })} v={`+€${p.floor_surcharge_excl_vat.toFixed(2)}`} />}
                {p.discount_amount > 0 && <Line k={t("book_v2.estimate.discount", { label: p.discount?.code || p.discount?.type || "" })} v={`−€${p.discount_amount.toFixed(2)}`} highlight />}
                <Line k={t("book_v2.estimate.subtotal")} v={`€${p.discounted_subtotal_excl_vat?.toFixed(2)}`} />
                {p.vat_exempt
                  ? <Line k={p.vat_mention || "TVA non applicable"} v="€0.00" />
                  : <Line k={t("book_v2.estimate.vat", { pct: p.vat_pct })} v={`€${p.vat_amount?.toFixed(2)}`} />}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10 mt-2">
                  <span>{t("book_v2.estimate.total")}</span><span>€{p.total?.toFixed(2)}</span>
                </div>
                <div className="text-[11px] italic text-slate-300/80 text-right -mt-0.5" data-testid="book-estimate-duration">
                  {t("book_v2.estimate.duration_line", { h: fmtDuration(p.billable_hours || p.hours) })}
                </div>
                {creditApplied > 0 && (
                  <div className="mt-2 flex items-center justify-between text-sm bg-[#5BA83F]/20 ring-1 ring-[#5BA83F]/40 rounded-md px-3 py-1.5" data-testid="book-store-credit">
                    <span className="text-[#9BD478] font-semibold">{t("book_v2.estimate.store_credit", { defaultValue: "Store credit applied" })}</span>
                    <span className="font-bold">−€{creditApplied.toFixed(2)}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-slate-300">{t("book_v2.estimate.empty")}</div>
            )}
          </div>

          {!discountApplied && p && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-wide font-bold text-[#9BD478] mb-1.5">{t("book_v2.estimate.discount_code")}</div>
              <div className="flex gap-2">
                <input type="text" value={discountInput} onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                  placeholder={t("book_v2.estimate.discount_ph")} className="flex-1 bg-white/10 rounded-full px-3 py-1.5 text-xs placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#5BA83F]"
                  data-testid="book-discount-input-sticky" />
                <button type="button" onClick={applyDiscount} className="bg-[#5BA83F] hover:bg-[#4A8A33] text-xs font-semibold rounded-full px-3">{t("book_v2.review.apply")}</button>
              </div>
              {discountMsg && <div className={`text-[10px] mt-1 ${discountMsg.type === "success" ? "text-[#9BD478]" : "text-rose-300"}`}>{discountMsg.text}</div>}
            </div>
          )}
          {discountApplied && (
            <div className="mt-3 flex items-center justify-between bg-[#5BA83F]/20 ring-1 ring-[#5BA83F]/40 rounded-full px-3 py-1 text-xs">
              <span className="font-semibold text-[#9BD478]">{discountApplied.code}</span>
              <button type="button" onClick={removeDiscount} className="text-white/70 hover:text-white">{t("book_v2.review.remove")}</button>
            </div>
          )}
        </>
      )}
    </aside>
  );
}

function Line({ k, v, highlight }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-300">{k}</span>
      <span className={highlight ? "text-[#9BD478] font-semibold" : ""}>{v}</span>
    </div>
  );
}

function ConfirmationScreen({ t, c, isConfirm, nav }) {
  return (
    <div className="min-h-screen bg-slate-50 py-14 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl ring-1 ring-slate-200 p-8 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#E8F5E0] text-[#3F7A1F] flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#1B3A6B] mt-4">
          {isConfirm ? t("book_v2.confirmation.title_confirmed") : t("book_v2.confirmation.title_request")}
        </h1>
        <p className="text-sm text-slate-600 mt-2">
          {isConfirm ? t("book_v2.confirmation.body_confirmed") : t("book_v2.confirmation.body_request")}
        </p>
        <div className="text-left bg-slate-50 rounded-lg p-4 mt-6 text-sm space-y-1.5">
          {c.id && <Line2 k={t("book_v2.confirmation.reference")} v={c.id.slice(0,8).toUpperCase()} />}
          <Line2 k={t("book_v2.confirmation.service")} v={c.service_type} />
          <Line2 k={t("book_v2.confirmation.date")} v={`${c.date} · ${c.start_time}`} />
          {(c.billable_hours || c.hours) ? <Line2 k={t("book_v2.confirmation.duration")} v={fmtDuration(c.billable_hours || c.hours)} /> : null}
          <Line2 k={t("book_v2.confirmation.address")} v={`${c.address}, ${c.city}`} />
          {c.total && <Line2 k={t("book_v2.confirmation.total")} v={`€${Number(c.total).toFixed(2)}`} bold />}
          {c.discount_amount > 0 && <Line2 k={t("book_v2.confirmation.discount")} v={`−€${c.discount_amount.toFixed(2)}`} highlight />}
          {c.vat_amount && <Line2 k={t("book_v2.confirmation.vat")} v={`€${c.vat_amount.toFixed(2)}`} />}
        </div>
        <Button onClick={() => nav("/my-account")} className="mt-6 w-full bg-[#1B3A6B] hover:bg-[#152D54] text-white">{t("book_v2.confirmation.cta_account")}</Button>
      </div>
    </div>
  );
}
function Line2({ k, v, bold, highlight }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{k}</span>
      <span className={`${bold ? "font-bold text-[#1B3A6B]" : ""} ${highlight ? "text-emerald-700" : ""}`}>{v}</span>
    </div>
  );
}
